/**
 * Zoomable, pannable frame around the native assembly drawing.
 *
 * Zoom is viewBox scaling rather than a CSS transform, so the geometry and the labels stay
 * vector-crisp at every step instead of being resampled like the old `<img>` was.
 *
 * The wheel listener is registered non-passively and always calls `preventDefault`: inside
 * this frame the wheel means "zoom" and nothing else, so a scroll gesture can never chain
 * out to the tools page behind the modal.
 */

import { ActionButton } from '@/components/common/ActionButton';
import { AssemblyDrawingScene } from '@/components/dialogs/assemblyDrawing/AssemblyDrawingScene';
import type { DrawingLayout } from '@/components/dialogs/assemblyDrawing/drawingLayout';
import type { AssemblyDrawingSpec } from '@/types/assemblyDrawing.types';
import { ArrowsOut, MagnifyingGlassMinus, MagnifyingGlassPlus } from '@phosphor-icons/react';
import type { FC, MouseEvent as ReactMouseEvent } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface AssemblyDrawingViewportProps {
  spec: AssemblyDrawingSpec;
  layout: DrawingLayout;
  title: string;
  className?: string;
}

/** Fit-to-frame is the floor — the whole sheet is already visible at zoom 1. */
const MIN_ZOOM = 1;
const MAX_ZOOM = 12;
const ZOOM_STEP = 1.4;
const WHEEL_ZOOM_SENSITIVITY = 0.0022;

/** Faint drafting-paper dot grid behind the drawing. */
const DRAWING_SHEET_STYLE = {
  backgroundImage: 'radial-gradient(circle, rgb(15 23 42 / 0.08) 1px, transparent 1px)',
  backgroundSize: '16px 16px',
};

interface ViewState {
  zoom: number;
  centerX: number;
  centerY: number;
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

export const AssemblyDrawingViewport: FC<AssemblyDrawingViewportProps> = ({ spec, layout, title, className = '' }) => {
  const { t } = useTranslation();
  const frameRef = useRef<HTMLDivElement>(null);
  const base = layout.view;

  const fitted = useMemo<ViewState>(
    () => ({ zoom: MIN_ZOOM, centerX: base.x + base.width / 2, centerY: base.y + base.height / 2 }),
    [base]
  );
  const [viewState, setViewState] = useState<ViewState>(fitted);

  // A different assembly is a different sheet: start it fitted rather than inside the
  // window the previous drawing happened to be zoomed into.
  useEffect(() => {
    setViewState(fitted);
  }, [fitted]);

  /**
   * Keep the window inside the sheet: at zoom z it is 1/z of the full extent, so its
   * centre may travel at most half the leftover in each direction.
   */
  const withClampedCentre = useCallback(
    (next: ViewState): ViewState => {
      const width = base.width / next.zoom;
      const height = base.height / next.zoom;
      return {
        zoom: next.zoom,
        centerX: clamp(next.centerX, base.x + width / 2, base.x + base.width - width / 2),
        centerY: clamp(next.centerY, base.y + height / 2, base.y + base.height - height / 2),
      };
    },
    [base]
  );

  const view = useMemo(() => {
    const width = base.width / viewState.zoom;
    const height = base.height / viewState.zoom;
    return { x: viewState.centerX - width / 2, y: viewState.centerY - height / 2, width, height };
  }, [base, viewState]);

  /** Zoom by `factor`, keeping the drawing point at the given fraction of the frame put. */
  const zoomAtFraction = useCallback(
    (factor: number, fractionX: number, fractionY: number) => {
      setViewState((previous) => {
        const zoom = clamp(previous.zoom * factor, MIN_ZOOM, MAX_ZOOM);
        if (zoom === previous.zoom) return previous;
        const oldWidth = base.width / previous.zoom;
        const oldHeight = base.height / previous.zoom;
        const anchorX = previous.centerX - oldWidth / 2 + fractionX * oldWidth;
        const anchorY = previous.centerY - oldHeight / 2 + fractionY * oldHeight;
        const width = base.width / zoom;
        const height = base.height / zoom;
        return withClampedCentre({
          zoom,
          centerX: anchorX - fractionX * width + width / 2,
          centerY: anchorY - fractionY * height + height / 2,
        });
      });
    },
    [base, withClampedCentre]
  );

  /**
   * Where the pointer sits inside the *drawn* sheet, as a fraction of the viewBox.
   *
   * `preserveAspectRatio="xMidYMid meet"` letterboxes the sheet inside the frame, so the
   * pointer's fraction of the frame is not its fraction of the viewBox.
   */
  const frameFraction = useCallback(
    (clientX: number, clientY: number, rect: DOMRect, width: number, height: number) => {
      const scale = Math.min(rect.width / width, rect.height / height);
      const offsetX = (rect.width - width * scale) / 2;
      const offsetY = (rect.height - height * scale) / 2;
      return {
        fractionX: (clientX - rect.left - offsetX) / (width * scale),
        fractionY: (clientY - rect.top - offsetY) / (height * scale),
        scale,
      };
    },
    []
  );

  // Read through a ref so the wheel listener below is attached once per sheet instead of
  // being torn down and rebuilt on every notch of a zoom gesture.
  const zoomRef = useRef(viewState.zoom);
  zoomRef.current = viewState.zoom;

  // Registered by hand because React's onWheel is passive: only a non-passive listener may
  // call preventDefault, and without that the page behind the modal scrolls along.
  useEffect(() => {
    const frame = frameRef.current;
    if (!frame) return;
    const onWheel = (event: WheelEvent) => {
      event.preventDefault();
      const rect = frame.getBoundingClientRect();
      const width = base.width / zoomRef.current;
      const height = base.height / zoomRef.current;
      const { fractionX, fractionY } = frameFraction(event.clientX, event.clientY, rect, width, height);
      zoomAtFraction(Math.exp(-event.deltaY * WHEEL_ZOOM_SENSITIVITY), fractionX, fractionY);
    };
    frame.addEventListener('wheel', onWheel, { passive: false });
    return () => frame.removeEventListener('wheel', onWheel);
  }, [base, frameFraction, zoomAtFraction]);

  const [isPanning, setIsPanning] = useState(false);
  const panOrigin = useRef({ pointerX: 0, pointerY: 0, centerX: 0, centerY: 0, unitsPerPixel: 1 });

  const handleMouseDown = (event: ReactMouseEvent<HTMLDivElement>) => {
    const frame = frameRef.current;
    if (!frame || viewState.zoom <= MIN_ZOOM || event.button !== 0) return;
    const rect = frame.getBoundingClientRect();
    const { scale } = frameFraction(event.clientX, event.clientY, rect, view.width, view.height);
    panOrigin.current = {
      pointerX: event.clientX,
      pointerY: event.clientY,
      centerX: viewState.centerX,
      centerY: viewState.centerY,
      unitsPerPixel: 1 / scale,
    };
    setIsPanning(true);
  };

  const handleMouseMove = (event: ReactMouseEvent<HTMLDivElement>) => {
    if (!isPanning) return;
    const origin = panOrigin.current;
    setViewState((previous) =>
      withClampedCentre({
        zoom: previous.zoom,
        centerX: origin.centerX - (event.clientX - origin.pointerX) * origin.unitsPerPixel,
        centerY: origin.centerY - (event.clientY - origin.pointerY) * origin.unitsPerPixel,
      })
    );
  };

  const handleEndPan = () => {
    setIsPanning(false);
  };

  const handleReset = () => {
    setViewState(fitted);
  };

  const isFitted = viewState.zoom === MIN_ZOOM;

  return (
    <div
      ref={frameRef}
      className={`relative overflow-hidden rounded-xl border border-gray-200 bg-white ${className}`}
      style={{ ...DRAWING_SHEET_STYLE, cursor: isFitted ? 'default' : isPanning ? 'grabbing' : 'grab' }}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleEndPan}
      onMouseLeave={handleEndPan}
    >
      <AssemblyDrawingScene spec={spec} layout={layout} view={view} title={title} />

      <div className="absolute right-3 top-3 z-10 flex items-center gap-1 rounded-xl border border-gray-200 bg-white/90 p-1 shadow-md backdrop-blur-sm">
        <ActionButton
          variant="ghost"
          iconOnly={true}
          icon={<MagnifyingGlassMinus size={16} aria-hidden="true" />}
          onClick={() => zoomAtFraction(1 / ZOOM_STEP, 0.5, 0.5)}
          disabled={viewState.zoom <= MIN_ZOOM}
          title={t('dialogs.zoomOut')}
          aria-label={t('dialogs.zoomOut')}
        />
        <span className="w-12 text-center text-xs tabular-nums text-gray-500">{Math.round(viewState.zoom * 100)}%</span>
        <ActionButton
          variant="ghost"
          iconOnly={true}
          icon={<MagnifyingGlassPlus size={16} aria-hidden="true" />}
          onClick={() => zoomAtFraction(ZOOM_STEP, 0.5, 0.5)}
          disabled={viewState.zoom >= MAX_ZOOM}
          title={t('dialogs.zoomIn')}
          aria-label={t('dialogs.zoomIn')}
        />
        <ActionButton
          variant="ghost"
          iconOnly={true}
          icon={<ArrowsOut size={16} aria-hidden="true" />}
          onClick={handleReset}
          disabled={isFitted}
          title={t('dialogs.resetZoom')}
          aria-label={t('dialogs.resetZoom')}
        />
      </div>
    </div>
  );
};
